/**
 * Instrument History Utility
 * 
 * Manages a localStorage-based history of recently used instruments.
 * This is used when the chat feature flag is disabled to maintain
 * instrument history independently of chat sessions.
 */

const STORAGE_KEY_PREFIX = 'ntb:copilot:instrumentHistory:';
const MAX_HISTORY_SIZE = 20;

/**
 * Gets the storage key for a specific user
 * @param {string} userId
 * @returns {string}
 */
const getStorageKey = (userId) => {
    if (!userId) return null;
    return `${STORAGE_KEY_PREFIX}${userId}`;
};

/**
 * Gets the instrument history from localStorage for a specific user
 * @param {string} userId - The user ID to get history for
 * @returns {Array<{instrument: string, category: string, timestamp: number, label: string}>}
 */
export const getInstrumentHistory = (userId) => {
    try {
        const key = getStorageKey(userId);
        if (!key) return [];

        const stored = localStorage.getItem(key);
        if (!stored) return [];
        return JSON.parse(stored);
    } catch (error) {
        console.error('Error reading instrument history:', error);
        return [];
    }
};

/**
 * Adds an instrument to the user's history (most recent first)
 * Deduplicates by instrument name and maintains max size
 * @param {string} userId - The user ID associated with the history
 * @param {string} instrument - Instrument symbol (e.g., 'EUR_USD')
 * @param {string} category - Category (e.g., 'FOREX', 'CRYPTO')
 * @param {string} label - Display label (e.g., 'EUR/USD')
 */
export const addToInstrumentHistory = (userId, instrument, category, label = null) => {
    if (!userId || !instrument || !category) return;

    try {
        const history = getInstrumentHistory(userId);
        const key = getStorageKey(userId);
        if (!key) return history;

        // Remove existing entry for this instrument (if any)
        const filtered = history.filter(item => item.instrument !== instrument);

        // Add new entry at the beginning
        const newEntry = {
            instrument,
            category,
            label: label || instrument.replace('_', '/'),
            timestamp: Date.now()
        };

        const updated = [newEntry, ...filtered].slice(0, MAX_HISTORY_SIZE);

        localStorage.setItem(key, JSON.stringify(updated));
        return updated;
    } catch (error) {
        console.error('Error saving instrument history:', error);
        return getInstrumentHistory(userId);
    }
};

/**
 * Removes an instrument from user's history
 * @param {string} userId - The user ID
 * @param {string} instrument - Instrument symbol to remove
 */
export const removeFromInstrumentHistory = (userId, instrument) => {
    if (!userId || !instrument) return;

    try {
        const history = getInstrumentHistory(userId);
        const key = getStorageKey(userId);
        if (!key) return history;

        const filtered = history.filter(item => item.instrument !== instrument);
        localStorage.setItem(key, JSON.stringify(filtered));
        return filtered;
    } catch (error) {
        console.error('Error removing from instrument history:', error);
        return getInstrumentHistory(userId);
    }
};

/**
 * Clears all instrument history for a user
 * @param {string} userId - The user ID
 */
export const clearInstrumentHistory = (userId) => {
    if (!userId) return [];

    try {
        const key = getStorageKey(userId);
        if (key) {
            localStorage.removeItem(key);
        }
        return [];
    } catch (error) {
        console.error('Error clearing instrument history:', error);
        return [];
    }
};

// eslint-disable-next-line import/no-anonymous-default-export
const instrumentHistoryUtils = {
    getInstrumentHistory,
    addToInstrumentHistory,
    removeFromInstrumentHistory,
    clearInstrumentHistory
};

export default instrumentHistoryUtils;
